import { type GlobalErrorHandler } from '../types'
import { getLocale } from './Localization'

export class ApiError extends Error {
  public status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export class Fetch {
  endpoint = '/'
  headers: Record<string, string>
  onError?: GlobalErrorHandler

  constructor(
    endpoint: string,
    token: string,
    headers?: Record<string, string>,
    onError?: GlobalErrorHandler,
  ) {
    this.endpoint = endpoint
    const defaultHeaders = {
      Accept: 'application/json',
      'Accept-Language': getLocale(),
      'Content-Type': 'application/json',
      'MD-Session-Token': token,
    }
    this.headers = headers || defaultHeaders
    this.onError = onError
  }

  get = async (url: string) => {
    const options = {
      method: 'GET',
      headers: this.headers,
    }

    return fetch(`${this.endpoint}${url}`, options)
      .then((response) => {
        if (!response.ok) {
          throw new ApiError(
            `ApiGetRequestError: ${response.status} ${response.statusText}`,
            response.status,
          )
        }
        return response.text()
      })
      .then((text) => (text.length ? JSON.parse(text) : {}))
      .then((data) => data)
      .catch((err) => {
        this.onError?.(err, undefined, 'ApiGetRequestError')
        throw err
      })
  }

  post = async (url: string, data?: any) => {
    const body = data ? JSON.stringify(data) : undefined
    const options = {
      method: 'POST',
      headers: this.headers,
      body,
    }

    return fetch(`${this.endpoint}${url}`, options)
      .then((response) => {
        if (!response.ok) {
          throw new ApiError(
            `ApiPostRequestError: ${response.status} ${response.statusText}`,
            response.status,
          )
        }

        return response.text()
      })
      .then((text) => (text.length ? JSON.parse(text) : {}))
      .then((data) => data)
  }

  put = async (url: string, data: any) => {
    const body = JSON.stringify(data)
    const options = {
      method: 'PUT',
      headers: this.headers,
      body,
    }

    return fetch(`${this.endpoint}${url}`, options)
      .then((response) => {
        if (!response.ok) {
          throw new ApiError(
            `ApiPutRequestError: ${response.status} ${response.statusText}`,
            response.status,
          )
        }
        return response.text()
      })
      .then((text) => (text.length ? JSON.parse(text) : {}))
      .then((data) => data)
      .catch((err) => {
        if (window?.app?.config?.type === 'pulse') {
          this.onError?.(err, undefined, 'ApiPutRequestError')
        }
        throw err
      })
  }

  delete = async (url: string) => {
    const options = {
      method: 'DELETE',
      headers: this.headers,
    }

    return fetch(`${this.endpoint}${url}`, options)
      .then((response) => {
        if (!response.ok) {
          throw new ApiError(
            `ApiDeleteRequestError: ${response.status} ${response.statusText}`,
            response.status,
          )
        }
        return response.text()
      })
      .then((text) => (text.length ? JSON.parse(text) : {}))
      .then((data) => data)
  }
}

export default Fetch
